import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { WhatsAppSettings } from "./WhatsAppSettings";
import { OtpCodes } from "./OtpCodes";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export const SecurityTab = ({ email }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const type = show ? "text" : "password";

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed. Use the new password from your next login.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-security-tab" className="grid lg:grid-cols-2 gap-16">
      <div>
        <h2 className="font-display text-2xl tracking-tight mb-2">Security</h2>
        <p className="text-sm text-white/50 leading-relaxed max-w-md">
          Change the admin password before going live. The new password is saved permanently — it will not be reset by the server, and a confirmation is emailed to <span className="text-white">{email}</span>.
        </p>
        <ul className="mt-8 space-y-3 text-xs text-white/50">
          {["Minimum 8 characters", "Use a mix of letters, numbers and symbols", "You stay signed in on this device after the change"].map((t) => (
            <li key={t} className="flex gap-3"><ShieldCheck size={14} className="text-[#D4AF37] shrink-0" />{t}</li>
          ))}
        </ul>
      </div>
      <form onSubmit={submit} className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-8 space-y-5" data-testid="change-password-form">
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Change Password</p>
        <input data-testid="current-password-input" required type={type} placeholder="Current password" value={form.current_password} onChange={set("current_password")} className={inputCls} autoComplete="current-password" />
        <input data-testid="new-password-input" required type={type} minLength={8} placeholder="New password (min 8 characters)" value={form.new_password} onChange={set("new_password")} className={inputCls} autoComplete="new-password" />
        <input data-testid="confirm-password-input" required type={type} minLength={8} placeholder="Confirm new password" value={form.confirm} onChange={set("confirm")} className={inputCls} autoComplete="new-password" />
        <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow((s) => !s)} className="flex items-center gap-2 text-xs text-white/50 hover:text-[#D4AF37] transition-colors">
          {show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Hide" : "Show"} passwords
        </button>
        <button data-testid="change-password-submit" disabled={saving} className="w-full border border-[#D4AF37] text-[#D4AF37] py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Update Password"}
        </button>
      </form>
      <WhatsAppSettings />
      <OtpCodes />
    </div>
  );
};
